// Análisis de repositorios de GitHub con modelos de IA.
// El ANÁLISIS se hace en el navegador contra la API pública de GitHub (sin Agent ni backend).
// Los modelos detectados se ofrecen como instalables: el Agent del Mac los descarga de
// Hugging Face vía installAgentModel (snapshot_download). repo_id es editable por el usuario.

const GH = "https://api.github.com";

export const HF_REPO_RE = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;

export function parseRepoUrl(url) {
  if (!url) return null;
  const m = String(url)
    .trim()
    .match(/github\.com[/:]([^/]+)\/([^/#?\s]+)/i);
  if (!m) return null;
  return { owner: m[1], repo: m[2].replace(/\.git$/i, "") };
}

// Familias conocidas -> capacidad (badge) + plantilla de instalación (repo HF + motor del Agent).
// repo_id prellenado con un repositorio real; el usuario puede editarlo antes de instalar.
const CATALOG = [
  { re: /\bwan\s?2\.?[12]\b/i, name: "Wan 2.1 / 2.2", capability: "video",
    install: { repo_id: "Wan-AI/Wan2.1-T2V-1.3B", runtime: "ltx-2-mlx", type: "video", capabilities: ["text-to-video", "image-to-video"] } },
  { re: /\bhunyuan\s?video\b/i, name: "Hunyuan Video", capability: "video",
    install: { repo_id: "tencent/HunyuanVideo", runtime: "ltx-2-mlx", type: "video", capabilities: ["text-to-video"] } },
  { re: /\bltx[-\s]?2|ltxv|ltx\s?video\b/i, name: "LTX Video", capability: "video",
    install: { repo_id: "Lightricks/LTX-Video", runtime: "ltx-2-mlx", type: "video", capabilities: ["text-to-video", "image-to-video"] } },
  { re: /\bqwen\s?image\b/i, name: "Qwen Image", capability: "image",
    install: { repo_id: "mlx-community/qwen-image-edit-2511-8bit", runtime: "mlx-gen", type: "image", capabilities: ["image-edit"] } },
  { re: /\bz-?image\b/i, name: "Z-Image", capability: "image",
    install: { repo_id: "", runtime: "mflux", type: "image", capabilities: ["text-to-image"] } },
  { re: /\bflux\b/i, name: "Flux 1/2", capability: "image",
    install: { repo_id: "black-forest-labs/FLUX.1-schnell", runtime: "mflux", type: "image", capabilities: ["text-to-image"] } },
  { re: /\bhidream\b/i, name: "HiDream", capability: "image",
    install: { repo_id: "", runtime: "mflux", type: "image", capabilities: ["text-to-image"] } },
  { re: /\bstable\s?diffusion|sdxl\b/i, name: "Stable Diffusion", capability: "image",
    install: { repo_id: "stabilityai/stable-diffusion-xl-base-1.0", runtime: "mflux", type: "image", capabilities: ["text-to-image"] } },
  { re: /\bqwen3?\s?tts|ace\s?step|omnivoice|index\s?tts|chatterbox|stable\s?audio\b/i, name: "TTS / Audio", capability: "audio", install: null },
  { re: /\bllama|mistral|qwen\d|gemma|phi-?\d|deepseek\b/i, name: "LLM (texto GGUF)", capability: "text",
    install: { repo_id: "", runtime: "llama.cpp", type: "model", capabilities: ["chat"] } },
  { re: /\bagent|mcp\s?server|tool\s?use\b/i, name: "Agente / MCP", capability: "agent", install: null },
];

export const CAP_LABEL = { text: "Texto", agent: "Agente", image: "Imagen", video: "Vídeo", audio: "Audio" };

function b64decode(str) {
  try {
    return decodeURIComponent(
      atob(str.replace(/\n/g, ""))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    try {
      return atob(str.replace(/\n/g, ""));
    } catch {
      return "";
    }
  }
}

async function ghJson(path) {
  const res = await fetch(`${GH}${path}`, { headers: { Accept: "application/vnd.github+json" }, cache: "no-store" });
  if (res.status === 403) throw new Error("Límite de la API de GitHub alcanzado. Inténtalo en unos minutos.");
  if (res.status === 404) throw new Error("Repositorio no encontrado o privado.");
  if (!res.ok) throw new Error(`GitHub respondió HTTP ${res.status}`);
  return res.json();
}

// Adivina motor/tipo/capacidades del Agent a partir de un repo_id de Hugging Face.
export function guessRuntime(repoId) {
  const s = repoId.toLowerCase();
  if (/gguf/.test(s)) return { runtime: "llama.cpp", type: "model", capabilities: ["chat"] };
  if (/(qwen).*(image).*(edit)|image[-_]?edit/.test(s)) return { runtime: "mlx-gen", type: "image", capabilities: ["image-edit"] };
  if (/sensenova/.test(s)) return { runtime: "sensenova-swift", type: "image", capabilities: ["image-edit"] };
  if (/\bltx|wan2|wan-2|hunyuan|video\b/.test(s)) return { runtime: "ltx-2-mlx", type: "video", capabilities: ["text-to-video", "image-to-video"] };
  if (/flux/.test(s)) return { runtime: "mflux", type: "image", capabilities: ["text-to-image"] };
  if (/qwen\d|llama|mistral|gemma|phi-?\d|deepseek/.test(s)) return { runtime: "llama.cpp", type: "model", capabilities: ["chat"] };
  return { runtime: "mflux", type: "image", capabilities: ["text-to-image"] };
}

const HF_EXCLUDE = new Set(["docs", "blog", "join", "pricing", "settings", "organizations", "models", "collections", "papers"]);

function extractHfModels(text) {
  const out = new Map();
  const re = /huggingface\.co\/(?:(datasets|spaces|models)\/)?([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)/gi;
  let m;
  while ((m = re.exec(text))) {
    const kind = (m[1] || "models").toLowerCase();
    if (kind === "datasets") continue;
    const owner = m[2];
    const model = m[3].replace(/[.)\]]+$/, "");
    if (HF_EXCLUDE.has(owner.toLowerCase())) continue;
    const repo_id = `${owner}/${model}`;
    if (out.has(repo_id)) continue;
    out.set(repo_id, { name: model, repo_id, source: "link", ...guessRuntime(repo_id) });
  }
  return [...out.values()];
}

function buildInstallPlan({ owner, repo, rootNames, readme }) {
  const has = (n) => rootNames.some((x) => x.toLowerCase() === n);
  const clone = `git clone https://github.com/${owner}/${repo}.git`;
  const deps = has("requirements.txt")
    ? "pip install -r requirements.txt"
    : has("setup.py") || has("pyproject.toml")
    ? "pip install -e ."
    : null;
  let run = null;
  const runMatch = readme.match(/python3?\s+([a-z0-9_\-/]+\.py)[^\n]*/i);
  if (runMatch) run = `python ${runMatch[1]}`;
  else {
    const entry = ["wgp.py", "app.py", "main.py", "run.py", "server.py", "webui.py"].find((f) =>
      rootNames.some((x) => x.toLowerCase() === f)
    );
    if (entry) run = `python ${entry}`;
  }
  const env = deps ? `python -m venv .venv && source .venv/bin/activate` : null;
  return { clone, env, deps, run };
}

function detectCategory({ rootNames, ggufFiles }) {
  if (ggufFiles.length) return "gguf";
  if (rootNames.some((n) => ["requirements.txt", "setup.py", "pyproject.toml"].includes(n.toLowerCase()))) return "python-app";
  return "generic";
}

// Analiza un repo y devuelve metadatos, familias (badges), modelos instalables y plan.
export async function analyzeRepo(url) {
  const parsed = parseRepoUrl(url);
  if (!parsed) throw new Error("URL de GitHub no válida. Ejemplo: https://github.com/usuario/repo");
  const { owner, repo } = parsed;

  const [meta, root, readmeRaw] = await Promise.all([
    ghJson(`/repos/${owner}/${repo}`),
    ghJson(`/repos/${owner}/${repo}/contents`).catch(() => []),
    ghJson(`/repos/${owner}/${repo}/readme`).catch(() => null),
  ]);

  const rootArr = Array.isArray(root) ? root : [];
  const rootNames = rootArr.map((x) => x.name);
  const ggufFiles = rootNames.filter((n) => /\.gguf$/i.test(n));
  const readme = readmeRaw?.content ? b64decode(readmeRaw.content) : "";

  const category = detectCategory({ rootNames, ggufFiles });
  const installPlan = buildInstallPlan({ owner, repo, rootNames, readme });

  // Familias detectadas en el README (badges) + instalables desde la plantilla del catálogo.
  const families = [];
  const installable = [];
  const seenNames = new Set();
  const seenRepos = new Set();

  for (const entry of CATALOG) {
    if (!entry.re.test(readme)) continue;
    if (!seenNames.has(entry.name)) {
      families.push({ name: entry.name, capability: entry.capability });
      seenNames.add(entry.name);
    }
    if (entry.install) {
      const rid = entry.install.repo_id;
      if (!rid || !seenRepos.has(rid)) {
        installable.push({ name: entry.name, capability: entry.capability, source: "catalog", ...entry.install });
        if (rid) seenRepos.add(rid);
      }
    }
  }

  // Modelos de Hugging Face enlazados directamente en el README (los más fiables, primero).
  for (const hf of extractHfModels(readme)) {
    if (seenRepos.has(hf.repo_id)) continue;
    installable.unshift(hf);
    seenRepos.add(hf.repo_id);
  }

  // Archivos GGUF presentes en la raíz del propio repo.
  for (const f of ggufFiles) {
    const repo_id = `${owner}/${repo}`;
    if (seenRepos.has(repo_id)) continue;
    installable.push({ name: f, repo_id, source: "gguf", runtime: "llama.cpp", type: "model", capabilities: ["chat"], include: f });
    seenRepos.add(repo_id);
  }

  return {
    ok: true,
    url: `https://github.com/${owner}/${repo}`,
    owner,
    repo,
    meta: {
      description: meta.description || "",
      stars: meta.stargazers_count || 0,
      language: meta.language || "—",
      license: meta.license?.spdx_id || meta.license?.name || "—",
      updated: meta.pushed_at || null,
    },
    category,
    families,
    installable,
    installPlan,
  };
}
